import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { automunityAPI } from '../lib/api';
import { useAuthContext } from '../hooks/AuthContext';
import './Navigation.css';

interface NavigationProps {
  user?: any;
  signOut?: () => void;
}

interface NavItem {
  path: string;
  label: string;
  icon: string;
  page: string;
  badge?: boolean;
}

interface NavGroup {
  label: string;
  icon: string;
  pages: string[];
  children: NavItem[];
}

const Navigation: React.FC<NavigationProps> = ({ user, signOut }) => {
  const location = useLocation();
  const [hasActiveAlerts, setHasActiveAlerts] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});
  const { canView, loading: authLoading, error: authError } = useAuthContext();

  useEffect(() => {
    const checkAlerts = async () => {
      try {
        const res = await automunityAPI.getMonitorAlerts({ status: 'new' });
        const data = await res.body.json();
        const records = Array.isArray(data) ? data
          : Array.isArray((data as any)?.records) ? (data as any).records
          : typeof (data as any)?.records === 'string' ? JSON.parse((data as any).records) : [];
        setHasActiveAlerts(records.length > 0);
      } catch {
        // Silently fail — don't block nav rendering
      }
    };
    checkAlerts();
    const interval = setInterval(checkAlerts, 60000);
    return () => clearInterval(interval);
  }, []);

  // Auto-expand groups when their children are active
  useEffect(() => {
    const automationsPages = ['/automations', '/requests', '/cleanup', '/integrations'];
    const deploymentsPages = ['/changelog', '/deployments'];

    if (automationsPages.some(p => location.pathname === p || location.pathname.startsWith(p + '/'))) {
      setExpandedGroups(prev => ({ ...prev, automations: true }));
    }
    if (deploymentsPages.some(p => location.pathname === p || location.pathname.startsWith(p + '/'))) {
      setExpandedGroups(prev => ({ ...prev, deployments: true }));
    }
  }, [location.pathname]);

  const toggleGroup = (groupKey: string) => {
    setExpandedGroups(prev => ({ ...prev, [groupKey]: !prev[groupKey] }));
  };

  // Top-level standalone items
  const standaloneItems: NavItem[] = [
    { path: '/dashboard', label: 'Dashboard', icon: '🏠', page: 'dashboard' },
  ];

  // Grouped items
  const navGroups: (NavGroup & { key: string })[] = [
    {
      key: 'automations',
      label: 'Automations',
      icon: '⚙️',
      pages: ['automations', 'requests', 'cleanup', 'integrations', 'ai-opportunities'],
      children: [
        { path: '/automations', label: 'Automations List', icon: '📋', page: 'automations' },
        { path: '/requests', label: 'Requests', icon: '📝', page: 'requests' },
        { path: '/ai-opportunities', label: 'AI Opportunities', icon: '🤖', page: 'ai-opportunities' },
        { path: '/cleanup', label: 'Cleanup', icon: '🧹', page: 'cleanup' },
        { path: '/integrations', label: 'Integrations', icon: '🔌', page: 'integrations' },
      ],
    },
    {
      key: 'deployments',
      label: 'Deployments',
      icon: '🚀',
      pages: ['changelog', 'deployments'],
      children: [
        { path: '/changelog', label: 'Changelog', icon: '📝', page: 'changelog' },
        { path: '/deployments', label: 'Deployments', icon: '🚀', page: 'deployments' },
      ],
    },
  ];

  // Bottom standalone items
  const bottomItems: NavItem[] = [
    { path: '/monitor', label: 'Monitor', icon: '📡', page: 'monitor', badge: hasActiveAlerts },
    { path: '/chat', label: 'Chat', icon: '💬', page: 'chat' },
    { path: '/reports', label: 'Reports', icon: '📊', page: 'reports' },
    { path: '/outreach', label: 'Outreach', icon: '📣', page: 'outreach' },
    { path: '/users', label: 'Users', icon: '👥', page: 'users' },
    { path: '/settings', label: 'Settings', icon: '⚙️', page: 'settings' },
  ];

  const isItemVisible = (item: NavItem) => {
    if (authLoading || authError) return item.page === 'dashboard';
    return canView(item.page);
  };

  const isGroupVisible = (group: NavGroup) => {
    if (authLoading || authError) return false;
    return group.children.some(child => canView(child.page));
  };

  const isActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(path + '/');

  const isGroupActive = (group: NavGroup) =>
    group.children.some(child => isActive(child.path));

  const renderNavItem = (item: NavItem, indent = false) => (
    <li key={item.path} className={`${isActive(item.path) ? 'active' : ''} ${indent ? 'nav-child' : ''}`}>
      <Link to={item.path}>
        <span className="nav-icon">{item.icon}</span>
        <span className="nav-label">{item.label}</span>
        {item.badge && <span className="nav-alert-dot"></span>}
      </Link>
    </li>
  );

  return (
    <nav className="sidebar">
      <div className="sidebar-header">
        <h2>Automunity</h2>
        <p>Building A Community of Automation Champions</p>
      </div>
      
      <ul className="nav-menu">
        {/* Standalone top items */}
        {standaloneItems.filter(isItemVisible).map(item => renderNavItem(item))}

        {/* Grouped items */}
        {navGroups.filter(isGroupVisible).map(group => (
          <li key={group.key} className={`nav-group ${isGroupActive(group) ? 'group-active' : ''}`}>
            <button
              className={`nav-group-toggle ${expandedGroups[group.key] ? 'expanded' : ''}`}
              onClick={() => toggleGroup(group.key)}
            >
              <span className="nav-icon">{group.icon}</span>
              <span className="nav-label">{group.label}</span>
              <span className="nav-chevron">{expandedGroups[group.key] ? '▾' : '▸'}</span>
            </button>
            {expandedGroups[group.key] && (
              <ul className="nav-submenu">
                {group.children.filter(child => canView(child.page)).map(child => renderNavItem(child, true))}
              </ul>
            )}
          </li>
        ))}

        {/* Standalone bottom items */}
        {bottomItems.filter(isItemVisible).map(item => renderNavItem(item))}
      </ul>
      
      {user && signOut && (
        <div className="user-section">
          <p className="user-name">{user.username}</p>
          <button onClick={signOut} className="sign-out-btn">Sign Out</button>
        </div>
      )}
    </nav>
  );
};

export default Navigation;